import { ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { userHeaderInterceptor } from '../security/user-header.interceptor';
import { authTokenInterceptor } from '../security/auth-token.interceptor';
import { ApiModule } from '../generated/openapi';
import { environment } from '../environments/environment';
import { ClarityModule, ClrIconModule } from '@clr/angular';
import { AuthConfig, OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import { AuthenticationService } from '../security/authentication.service';
import { APOLLO_NAMED_OPTIONS, NamedOptions, provideApollo } from 'apollo-angular';
import { ApolloClientOptions, InMemoryCache, split } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { Kind } from 'graphql';
import { HttpLink } from 'apollo-angular/http';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { setContext } from '@apollo/client/link/context';

const authConfig: AuthConfig = {
  issuer: environment.cognitoAuthority,
  redirectUri: window.location.origin,
  clientId: environment.cognitoClientId,
  responseType: 'code',
  scope: 'openid email profile',
  requireHttps: false,
  showDebugInformation: true,
  strictDiscoveryDocumentValidation: false,
  logoutUrl: environment.cognitoLogoutUrl,
  oidc: true
};

export function createNamedApollo(httpLink: HttpLink, oauthService: OAuthService): NamedOptions {
  // HTTP-Link (HotChocolate-Backend)
  const http = httpLink.create({
    uri: environment.backendGraphQLUrl, // http://localhost:5089/graphql/
    withCredentials: false,
  });

  // WebSocket-Link (für Subscriptions)
  const ws = new GraphQLWsLink(
    createClient({
      url: environment.backendGraphQLUrl.replace(/^http/, 'ws'), // ws://localhost:5089/graphql/
      // Der Browser sendet beim WebSocket-Handshake keinen Header, das Token geht mit
      // connection_init. Wird bei jedem Verbindungsaufbau neu gelesen.
      connectionParams: () => ({ authorization: `Bearer ${oauthService.getAccessToken()}` }),
    })
  );

  // Split-Link: je nach Operation HTTP oder WS
  const splitLink = split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return def.kind === Kind.OPERATION_DEFINITION && def.operation === 'subscription';
    },
    ws,
    http
  );

  return {
    hotchocolate: {
      cache: new InMemoryCache(),
      link: splitLink,
    },
  };
}

export const appConfig: ApplicationConfig = {
  // NOTE: provideHttpClient appears twice (pre-existing); the LAST call wins
  // for feature configuration, so the interceptors are attached to BOTH to
  // survive the override.
  providers: [provideRouter(routes), provideHttpClient(withInterceptorsFromDi(), withInterceptors([userHeaderInterceptor, authTokenInterceptor])), importProvidersFrom(ApiModule.forRoot({ rootUrl: environment.API_Base }), ClarityModule, ClrIconModule), provideAnimations(),
    provideOAuthClient(),
    AuthenticationService,
    { provide: AuthConfig, useValue: authConfig },
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([userHeaderInterceptor, authTokenInterceptor])),
    provideApollo((): ApolloClientOptions<any> => {
      const httpLink = inject(HttpLink);
      // API URL & KEY
      const API_URL = 'https://vquljw7kxrbtvfddqu7zcnfari.appsync-api.eu-central-1.amazonaws.com/graphql';
      const API_KEY = 'da2-ml7t2j66pbc2xozbj3vyix6qze';

      // Auth-Header für HTTP-Link setzen
      const authLink = setContext((_, { headers }) => ({
        headers: {
          ...headers,
          'x-api-key': API_KEY,
        }
      }));

      // HTTP-Link (mit Auth)
      const http = authLink.concat(httpLink.create({ uri: API_URL }));
     
      // Create a WebSocket link:
      const ws = new GraphQLWsLink(
        createClient({
          url: 'wss://vquljw7kxrbtvfddqu7zcnfari.appsync-realtime-api.eu-central-1.amazonaws.com/graphql',
          connectionParams: () => ({
            headers: {
              host: 'vquljw7kxrbtvfddqu7zcnfari.appsync-api.eu-central-1.amazonaws.com/graphql',
              'x-api-key': 'da2-ml7t2j66pbc2xozbj3vyix6qze' // API Key
            }
        }),
    }));
     
      // Using the ability to split links, you can send data to each link
      // depending on what kind of operation is being sent
      const link = split(
        // Split based on operation type
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.operation === "subscription"
          );
        },
        ws,
        http,
      );
     
      return {
        link,
        cache: new InMemoryCache()
      };
    }),
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory: createNamedApollo,
      deps: [HttpLink, OAuthService]
    },
  ],
};
